import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { crmSetupService, crmLeadService, whatsappService, clientService, crmAutomationService } from '../services/api';
import { maskPhone, maskDate, isValidDate, dateToIso } from '../utils/masks';

export default function CRM({ initialMode = 'kanban' }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isChatbotPath = location.pathname === '/chatbot' || initialMode === 'inbox';
  const [isInboxOpen, setIsInboxOpen] = useState(isChatbotPath);

  useEffect(() => {
    if (location.pathname === '/chatbot') {
      setIsInboxOpen(true);
    } else if (location.pathname === '/crm') {
      setIsInboxOpen(false);
    }
  }, [location.pathname]);

  const [funnels, setFunnels] = useState([]);
  const [activeFunnel, setActiveFunnel] = useState(null);
  const [columns, setColumns] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const chatEndRef = useRef(null);

  // Convert Lead to Registered Client Modal
  const [isConvertModalOpen, setIsConvertModalOpen] = useState(false);
  const [convertForm, setConvertForm] = useState({ name: '', phone: '', birth_date: '', nickname: '' });
  const [converting, setConverting] = useState(false);

  // Automations Modal State
  const [isAutomationModalOpen, setIsAutomationModalOpen] = useState(false);
  const [selectedColForAutomation, setSelectedColForAutomation] = useState(null);
  const [automationsList, setAutomationsList] = useState([]);
  const [availableInstances, setAvailableInstances] = useState([]);
  const [automationForm, setAutomationForm] = useState({
    id: null,
    name: '',
    trigger_type: 'on_move',
    delay_minutes: 0,
    schedule_day: 'friday',
    schedule_time: '17:00',
    message_text: '',
    instance_name: '',
    anti_spam_delay_sec: 10
  });
  const [isEditingAutomation, setIsEditingAutomation] = useState(false);
  const [runningBroadcast, setRunningBroadcast] = useState(false);

  const scrollToBottom = () => {
    setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages.length, selectedLead?.id]);
  
  // Create Lead Modal
  const [isCreatingLead, setIsCreatingLead] = useState(false);
  const [newLeadForm, setNewLeadForm] = useState({ name: '', phone: '' });

  // Search filter
  const [searchTerm, setSearchTerm] = useState('');

  // Proposta Dinâmica Modal
  const [isProposalOpen, setIsProposalOpen] = useState(false);
  const [proposalTab, setProposalTab] = useState('products');
  const [catalogItems, setCatalogItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [customAmount, setCustomAmount] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [includePayment, setIncludePayment] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState('mercadopago');
  const [isSendingProposal, setIsSendingProposal] = useState(false);

  const openProposalModal = async () => {
    setIsProposalOpen(true);
    try {
      const res = await whatsappService.getCatalog();
      setCatalogItems(res.data || []);
    } catch (e) {
      console.error('Erro ao carregar catálogo', e);
    }
  };

  const handleToggleItem = (item) => {
    setSelectedItems(prev => {
      const next = { ...prev };
      if (next[item.id]) {
        delete next[item.id];
      } else {
        next[item.id] = { ...item, qty: 1 };
      }
      return next;
    });
  };

  const handleQtyChange = (itemId, delta) => {
    setSelectedItems(prev => {
      if (!prev[itemId]) return prev;
      const newQty = Math.max(1, prev[itemId].qty + delta);
      return {
        ...prev,
        [itemId]: { ...prev[itemId], qty: newQty }
      };
    });
  };

  const handleSendProposal = async () => {
    if (!selectedLead) return;
    setIsSendingProposal(true);
    try {
      const itemsList = Object.values(selectedItems);
      await whatsappService.sendProposal(selectedLead.id, {
        items: proposalTab === 'products' ? itemsList : [],
        custom_amount: proposalTab === 'custom' ? customAmount : null,
        custom_description: proposalTab === 'custom' ? customDescription : null,
        include_payment: includePayment,
        payment_method: paymentMethod
      });

      const msgsRes = await whatsappService.getMessages(selectedLead.id);
      setMessages(msgsRes.data);
      
      setIsProposalOpen(false);
      setSelectedItems({});
      setCustomAmount('');
      setCustomDescription('');

      // Recarrega lista de leads para refletir novo valor e etiqueta Proposta Pendente
      if (activeFunnel) {
        const leadRes = await crmLeadService.getLeads(activeFunnel);
        setLeads(leadRes.data);
        const updated = leadRes.data.find(l => l.id === selectedLead.id);
        if (updated) setSelectedLead(updated);
      }

      alert('Proposta gerada e enviada para o WhatsApp do cliente!');
    } catch (e) {
      alert('Erro ao gerar proposta: ' + (e.response?.data?.error || e.message));
    } finally {
      setIsSendingProposal(false);
    }
  };

  const handleToggleProposalStatus = async (status) => {
    if (!selectedLead) return;
    try {
      await crmLeadService.updateProposalStatus(selectedLead.id, status);
      if (activeFunnel) {
        const leadRes = await crmLeadService.getLeads(activeFunnel);
        setLeads(leadRes.data);
        const updated = leadRes.data.find(l => l.id === selectedLead.id);
        if (updated) setSelectedLead(updated);
      }
    } catch (e) {
      alert('Erro ao atualizar status da proposta: ' + (e.response?.data?.error || e.message));
    }
  };

  // WhatsApp Config
  const [isWhatsAppConfigOpen, setIsWhatsAppConfigOpen] = useState(false);
  const [waStatus, setWaStatus] = useState('loading'); // 'loading', 'connected', 'disconnected'
  const [waQrCode, setWaQrCode] = useState(null);
  const [waInstanceName, setWaInstanceName] = useState('');
  const [waOwnerPhone, setWaOwnerPhone] = useState('');
  
  // Tags State
  const [isTagsModalOpen, setIsTagsModalOpen] = useState(false);
  // Column Edit Modal State
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);
  const [editingColumn, setEditingColumn] = useState(null);
  const [colForm, setColForm] = useState({ name: '', color: '#3b82f6', sort_order: 0 });

  const handleOpenCreateColumn = (sortOrder = 0) => {
    setEditingColumn(null);
    const targetSort = sortOrder > 0 ? sortOrder : Math.max(1, columns.length - 1);
    setColForm({ name: '', color: '#3b82f6', sort_order: targetSort });
    setIsColumnModalOpen(true);
  };

  const handleOpenEditColumn = (col) => {
    setEditingColumn(col);
    setColForm({ name: col.name, color: col.color || '#3b82f6', sort_order: col.sort_order || 0 });
    setIsColumnModalOpen(true);
  };

  const handleSaveColumn = async (e) => {
    e.preventDefault();
    if (!colForm.name.trim()) return alert('Informe o nome da coluna');
    if (!activeFunnel) return alert('Nenhum funil selecionado');

    try {
      if (editingColumn) {
        await crmSetupService.updateColumn(editingColumn.id, {
          name: colForm.name.trim(),
          color: colForm.color,
          sort_order: colForm.sort_order
        });
      } else {
        await crmSetupService.createColumn({
          funnel_id: activeFunnel,
          name: colForm.name.trim(),
          color: colForm.color,
          sort_order: colForm.sort_order
        });
      }
      setIsColumnModalOpen(false);
      const [colRes, leadRes] = await Promise.all([
        crmSetupService.getColumns(activeFunnel),
        crmLeadService.getLeads(activeFunnel)
      ]);
      setColumns(colRes.data);
      setLeads(leadRes.data);
    } catch (err) {
      alert('Erro ao salvar coluna');
    }
  };

  const handleDeleteColumn = async () => {
    if (!editingColumn) return;
    const isFixed = columns.length > 0 && (editingColumn.id === columns[0].id || editingColumn.id === columns[columns.length - 1].id);
    if (isFixed) {
      return alert('A coluna "Caixa de Entrada" (1ª) e "Venda Ganha" (última) são colunas fixas do sistema e não podem ser excluídas.');
    }
    if (!window.confirm(`Deseja excluir a coluna "${editingColumn.name}"?`)) return;
    try {
      await crmSetupService.deleteColumn(editingColumn.id);
      setIsColumnModalOpen(false);
      const [colRes, leadRes] = await Promise.all([
        crmSetupService.getColumns(activeFunnel),
        crmLeadService.getLeads(activeFunnel)
      ]);
      setColumns(colRes.data);
      setLeads(leadRes.data);
    } catch (err) {
      alert('Erro ao excluir coluna');
    }
  };

  const formatCurrency = (val) => {
    const num = Number(val) || 0;
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const safeInitials = (lead) => {
    if (!lead) return 'LD';
    const str = lead.client_name || lead.name || lead.phone || 'Lead';
    const clean = String(str).trim();
    return (clean.substring(0, 2) || 'LD').toUpperCase();
  };

  const LeadAvatar = ({ lead, size = 36 }) => {
    const [imgError, setImgError] = useState(false);
    const avatarUrl = lead?.avatar_url || lead?.client_avatar_url;
    const name = lead?.client_name || lead?.name || lead?.phone || 'Lead';
    const initials = safeInitials(lead);

    const getColor = (str) => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
      const colors = ['#10b981', '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#06b6d4', '#3b82f6', '#f59e0b'];
      return colors[Math.abs(hash) % colors.length];
    };

    if (avatarUrl && !imgError) {
      return (
        <img
          src={avatarUrl}
          alt={name}
          onError={() => setImgError(true)}
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            objectFit: 'cover',
            border: '1px solid rgba(255,255,255,0.15)',
            flexShrink: 0
          }}
        />
      );
    }

    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: getColor(name),
          color: '#fff',
          fontWeight: 'bold',
          fontSize: Math.max(10, Math.round(size * 0.4)),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          border: '1px solid rgba(255,255,255,0.15)',
          textTransform: 'uppercase'
        }}
      >
        {initials}
      </div>
    );
  };

  const safeLeadTime = (dateVal) => {
    if (!dateVal) return '';
    try {
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } catch (_) {
      return '';
    }
  };

  const handleSelectLead = (lead) => {
    if (!lead) return;
    setSelectedLead(lead);
    setIsInboxOpen(true);
    if (lead.id) {
      loadLeadNotes(lead.id);
    }
  };

  // Notes State
  const [leadNotes, setLeadNotes] = useState([]);
  const [newNoteContent, setNewNoteContent] = useState('');

  const loadLeadNotes = async (leadId) => {
    if (!leadId) return setLeadNotes([]);
    try {
      const res = await crmLeadService.getNotes(leadId);
      setLeadNotes(res.data || []);
    } catch (e) {
      console.error('Erro ao carregar notas do lead', e);
    }
  };

  useEffect(() => {
    if (selectedLead?.id) {
      loadLeadNotes(selectedLead.id);
    } else {
      setLeadNotes([]);
    }
  }, [selectedLead?.id]);

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) return alert('Escreva uma nota antes de adicionar');
    if (!selectedLead) return;
    try {
      await crmLeadService.addNote(selectedLead.id, newNoteContent.trim());
      setNewNoteContent('');
      await loadLeadNotes(selectedLead.id);
    } catch (err) {
      alert('Erro ao adicionar nota');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Deseja excluir esta nota?')) return;
    try {
      await crmLeadService.deleteNote(selectedLead.id, noteId);
      await loadLeadNotes(selectedLead.id);
    } catch (err) {
      alert('Erro ao excluir nota');
    }
  };
  // Custom Fields State
  const [customFields, setCustomFields] = useState([]);
  const [leadCustomValues, setLeadCustomValues] = useState({});
  const [isCustomFieldModalOpen, setIsCustomFieldModalOpen] = useState(false);
  const [editingCustomField, setEditingCustomField] = useState(null);
  const [customFieldForm, setCustomFieldForm] = useState({ name: '', field_type: 'text', width: 'full', options: '' });

  const loadCustomFields = async () => {
    try {
      const res = await crmSetupService.getCustomFields();
      setCustomFields(res.data || []);
    } catch (e) {
      console.error('Erro ao carregar campos personalizados', e);
    }
  };

  const loadLeadCustomValues = async (leadId) => {
    if (!leadId) return setLeadCustomValues({});
    try {
      const res = await crmLeadService.getCustomValues(leadId);
      setLeadCustomValues(res.data || {});
    } catch (e) {
      console.error('Erro ao carregar valores personalizados do lead', e);
    }
  };

  useEffect(() => {
    loadCustomFields();
  }, []);

  useEffect(() => {
    if (selectedLead?.id) {
      loadLeadCustomValues(selectedLead.id);
    } else {
      setLeadCustomValues({});
    }
  }, [selectedLead?.id]);

  const handleSaveCustomFieldValue = async (fieldId, val) => {
    if (!selectedLead) return;
    const updated = { ...leadCustomValues, [fieldId]: val };
    setLeadCustomValues(updated);
    try {
      await crmLeadService.updateCustomValues(selectedLead.id, { [fieldId]: val });
    } catch (e) {
      console.error('Erro ao salvar valor do campo personalizado', e);
    }
  };

  const handleOpenCreateCustomField = () => {
    setEditingCustomField(null);
    setCustomFieldForm({ name: '', field_type: 'text', width: 'full', options: '' });
    setIsCustomFieldModalOpen(true);
  };

  const handleOpenEditCustomField = (field) => {
    setEditingCustomField(field);
    setCustomFieldForm({
      name: field.name,
      field_type: field.field_type || 'text',
      width: field.width || 'full',
      options: field.options || ''
    });
    setIsCustomFieldModalOpen(true);
  };

  const handleSaveCustomFieldDefinition = async (e) => {
    e.preventDefault();
    if (!customFieldForm.name.trim()) return alert('Informe o nome do campo');
    try {
      if (editingCustomField) {
        await crmSetupService.updateCustomField(editingCustomField.id, customFieldForm);
      } else {
        await crmSetupService.createCustomField(customFieldForm);
      }
      setIsCustomFieldModalOpen(false);
      await loadCustomFields();
    } catch (err) {
      alert('Erro ao salvar campo personalizado');
    }
  };

  const handleDeleteCustomField = async (fieldId) => {
    if (!window.confirm('Deseja excluir este campo personalizado? Os dados salvos nos leads serão perdidos.')) return;
    try {
      await crmSetupService.deleteCustomField(fieldId);
      setIsCustomFieldModalOpen(false);
      await loadCustomFields();
    } catch (err) {
      alert('Erro ao apagar campo personalizado');
    }
  };

  const [tagsList, setTagsList] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#3b82f6');

  const presetColors = [
    '#ef4444', '#f97316', '#f59e0b', '#10b981', '#06b6d4', 
    '#3b82f6', '#8b5cf6', '#ec4899', '#6b7280', '#111827'
  ];

  const loadTags = async () => {
    try {
      const res = await crmSetupService.getTags();
      setTagsList(res.data || []);
    } catch (e) {
      console.error('Erro ao carregar etiquetas', e);
    }
  };

  useEffect(() => {
    loadTags();
  }, []);

  const getLeadTags = (lead) => {
    if (!lead || !lead.tags) return [];
    let arr = lead.tags;
    if (typeof arr === 'string') {
      try { arr = JSON.parse(arr); } catch (e) { arr = []; }
    }
    return Array.isArray(arr) ? arr.filter(Boolean) : [];
  };

  const handleCreateTag = async (e) => {
    e.preventDefault();
    if (!newTagName.trim()) return alert('Informe o nome da etiqueta');
    try {
      await crmSetupService.createTag({ name: newTagName.trim(), color: newTagColor });
      setNewTagName('');
      await loadTags();
      if (activeFunnel) {
        const leadRes = await crmLeadService.getLeads(activeFunnel);
        setLeads(leadRes.data);
      }
    } catch (err) {
      alert('Erro ao criar etiqueta');
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (!window.confirm('Deseja excluir esta etiqueta?')) return;
    try {
      await crmSetupService.deleteTag(tagId);
      await loadTags();
      if (activeFunnel) {
        const leadRes = await crmLeadService.getLeads(activeFunnel);
        setLeads(leadRes.data);
      }
    } catch (err) {
      alert('Erro ao apagar etiqueta');
    }
  };

  const handleAddTagToLead = async (leadId, tagId) => {
    try {
      await crmLeadService.addTag(leadId, tagId);
      const leadRes = await crmLeadService.getLeads(activeFunnel);
      setLeads(leadRes.data);
      const updated = leadRes.data.find(l => l.id === leadId);
      if (updated) setSelectedLead(updated);
    } catch (err) {
      alert('Erro ao vincular etiqueta');
    }
  };

  const handleRemoveTagFromLead = async (leadId, tagId) => {
    try {
      await crmLeadService.removeTag(leadId, tagId);
      const leadRes = await crmLeadService.getLeads(activeFunnel);
      setLeads(leadRes.data);
      const updated = leadRes.data.find(l => l.id === leadId);
      if (updated) setSelectedLead(updated);
    } catch (err) {
      alert('Erro ao remover etiqueta');
    }
  };

  // Load Funnels
  useEffect(() => {
    crmSetupService.getFunnels().then(r => {
      setFunnels(r.data);
      if (r.data.length > 0) setActiveFunnel(r.data[0].id);
    });
  }, []);

  // Load Columns and Leads when funnel changes (and poll)
  useEffect(() => {
    if (!activeFunnel) return;
    
    const loadBoard = () => {
      Promise.all([
        crmSetupService.getColumns(activeFunnel),
        crmLeadService.getLeads(activeFunnel)
      ]).then(([colRes, leadRes]) => {
        setColumns(colRes.data);
        setLeads(leadRes.data);
        setLoading(false);
      });
    };
    
    setLoading(true);
    loadBoard();
    
    const interval = setInterval(loadBoard, 15000);
    return () => clearInterval(interval);
  }, [activeFunnel]);

  // Load chat messages when a lead is selected
  useEffect(() => {
    if (!selectedLead?.id) {
      setMessages([]);
      return;
    }
    whatsappService.getMessages(selectedLead.id)
      .then(r => setMessages(Array.isArray(r.data) ? r.data : []))
      .catch(e => { console.error('Erro ao carregar chat', e); setMessages([]); });
      
    const interval = setInterval(() => {
      whatsappService.getMessages(selectedLead.id)
        .then(r => setMessages(Array.isArray(r.data) ? r.data : []))
        .catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, [selectedLead?.id]);

  const handleSendMessage = async () => {
    if (!chatInput.trim() || !selectedLead) return;
    try {
      const text = chatInput.trim();
      setChatInput(''); // optimistic clear
      
      // Add optimistically
      const optimisticMsg = { id: Date.now(), direction: 'outbound', message_text: text, created_at: new Date().toISOString() };
      setMessages(prev => [...prev, optimisticMsg]);
      scrollToBottom();
      
      await whatsappService.sendMessage(selectedLead.id, text);
    } catch (e) {
      alert('Erro ao enviar mensagem');
    }
  };

  const handleCreateLead = async (e) => {
    e.preventDefault();
    if (!newLeadForm.name || !newLeadForm.phone) return alert('Preencha nome e telefone');
    if (!activeFunnel || columns.length === 0) return alert('Nenhum funil ou coluna disponível');
    
    try {
      const payload = {
        funnel_id: activeFunnel,
        column_id: columns[0].id,
        name: newLeadForm.name,
        phone: newLeadForm.phone,
        value: parseFloat(newLeadForm.value) || 0.00
      };
      await crmLeadService.createLead(payload);
      
      // Reload leads
      const leadRes = await crmLeadService.getLeads(activeFunnel);
      setLeads(leadRes.data);
      
      setIsCreatingLead(false);
      setNewLeadForm({ name: '', phone: '', value: '' });
    } catch (err) {
      alert('Erro ao criar lead');
    }
  };

  const handleOpenConvertModal = () => {
    if (!selectedLead) return;
    setConvertForm({
      name: selectedLead.name || '',
      phone: selectedLead.phone || '',
      birth_date: '',
      nickname: ''
    });
    setIsConvertModalOpen(true);
  };

  const handleConvertLeadToClient = async (e) => {
    e.preventDefault();
    if (!convertForm.name || !convertForm.name.trim()) return alert('Informe o nome');
    const cleanDate = (convertForm.birth_date || '').replace(/\D/g, '');
    if (cleanDate.length !== 8 || !isValidDate(convertForm.birth_date)) {
      return alert('A data de aniversário deve estar no formato DD/MM/AAAA (ex: 15/08/1995)');
    }
    setConverting(true);
    try {
      const res = await clientService.create({
        name: convertForm.name,
        phone: convertForm.phone,
        birth_date: dateToIso(convertForm.birth_date),
        nickname: convertForm.nickname || null
      });
      const newClientId = res.data.id;
      await crmLeadService.updateLead(selectedLead.id, { client_id: newClientId });
      setIsConvertModalOpen(false);

      const updatedLead = { ...selectedLead, client_id: newClientId, client_name: convertForm.name };
      setSelectedLead(updatedLead);
      setLeads(prev => prev.map(l => l.id === selectedLead.id ? updatedLead : l));
      alert('🎉 Lead convertido e cadastrado como Cliente no sistema com sucesso!');
    } catch (err) {
      alert('Erro ao converter lead em cliente: ' + (err.response?.data?.error || err.message));
    } finally {
      setConverting(false);
    }
  };

  // Automation Handlers
  const handleOpenAutomationModal = async (col) => {
    setSelectedColForAutomation(col);
    setIsEditingAutomation(false);

    try {
      const instRes = await whatsappService.getInstances();
      setAvailableInstances(instRes.data || []);
    } catch (e) {}

    setAutomationForm({
      id: null,
      name: `Disparo - ${col.name}`,
      trigger_type: 'on_move',
      delay_minutes: 0,
      schedule_day: 'friday',
      schedule_time: '17:00',
      message_text: 'Olá {nome}! Sextouuuu Bora pra Arena Cyber Café! 🚀🎮',
      instance_name: waInstanceName || 'CRMPages_5533998420631',
      anti_spam_delay_sec: 10
    });
    setIsAutomationModalOpen(true);
    try {
      const res = await crmAutomationService.getAutomations({ column_id: col.id });
      setAutomationsList(res.data || []);
    } catch (e) {
      console.error('Erro ao carregar automações:', e);
    }
  };

  const handleSaveAutomation = async (e) => {
    e.preventDefault();
    if (!automationForm.name.trim() || !automationForm.message_text.trim()) {
      return alert('Preencha o nome da rotina e o texto da mensagem');
    }
    try {
      const payload = {
        ...automationForm,
        funnel_id: activeFunnel,
        column_id: selectedColForAutomation.id
      };
      if (automationForm.id) {
        await crmAutomationService.updateAutomation(automationForm.id, payload);
      } else {
        await crmAutomationService.createAutomation(payload);
      }
      setIsEditingAutomation(false);
      const res = await crmAutomationService.getAutomations({ column_id: selectedColForAutomation.id });
      setAutomationsList(res.data || []);
      alert('⚡ Automação salva com sucesso!');
    } catch (err) {
      alert('Erro ao salvar automação: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteAutomation = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta automação?')) return;
    try {
      await crmAutomationService.deleteAutomation(id);
      setAutomationsList(prev => prev.filter(a => a.id !== id));
      if (automationForm.id === id) setIsEditingAutomation(false);
    } catch (err) {
      alert('Erro ao excluir automação');
    }
  };

  const handleTriggerAutomationNow = async (id) => {
    if (!window.confirm('Deseja iniciar o disparo agora para TODOS os leads desta coluna? As mensagens serão enviadas respeitando o intervalo anti-spam.')) return;
    setRunningBroadcast(true);
    try {
      const res = await crmAutomationService.triggerNow(id);
      alert('🚀 Disparo iniciado: ' + res.data.message);
    } catch (err) {
      alert('Erro ao iniciar disparo: ' + (err.response?.data?.error || err.message));
    } finally {
      setRunningBroadcast(false);
    }
  };

  // WhatsApp Setup & Polling
  const openWhatsAppConfig = async () => {
    setIsWhatsAppConfigOpen(true);
    setWaStatus('loading');
    try {
      const res = await whatsappService.getInstance();
      const instName = res.data?.instanceName || res.data?.instance?.instanceName || `tenant_${activeFunnel || 1}`;
      setWaInstanceName(instName);
      if (res.data?.instance?.owner) {
        setWaOwnerPhone(res.data.instance.owner.split('@')[0]);
      }
      if (res.data?.status === 'connected' || res.data?.status === 'open' || res.data?.instance?.state === 'open') {
        setWaStatus('connected');
        setWaQrCode(null);
      } else if (res.data?.status === 'qr') {
        setWaStatus('disconnected');
        setWaQrCode(res.data?.qrcode?.base64 || res.data?.qrcode);
      } else {
        setWaStatus('disconnected');
        setWaQrCode(null);
      }
    } catch (e) {
      setWaStatus('disconnected');
    }
  };

  useEffect(() => {
    let interval;
    if (isWhatsAppConfigOpen && waStatus === 'disconnected' && waQrCode) {
      interval = setInterval(async () => {
        try {
          const res = await whatsappService.getInstance();
          const instName = res.data?.instanceName || res.data?.instance?.instanceName || `tenant_${activeFunnel || 1}`;
          setWaInstanceName(instName);
          if (res.data?.instance?.owner) setWaOwnerPhone(res.data.instance.owner.split('@')[0]);
          if (res.data?.status === 'connected' || res.data?.status === 'open' || res.data?.instance?.state === 'open') {
            setWaStatus('connected');
            setWaQrCode(null);
          }
        } catch (e) {}
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [isWhatsAppConfigOpen, waStatus, waQrCode, activeFunnel]);

  const handleGenerateQr = async () => {
    setWaStatus('loading');
    try {
      const res = await whatsappService.createInstance();
      if (res.data?.qrcode?.base64) {
        setWaQrCode(res.data.qrcode.base64);
        setWaStatus('disconnected');
      } else if (res.data?.status === 'connected') {
        setWaStatus('connected');
      }
    } catch (e) {
      alert('Erro ao gerar QR Code');
      setWaStatus('disconnected');
    }
  };

  const handleDisconnectQr = async () => {
    if (!window.confirm('Deseja realmente desconectar o WhatsApp e apagar a instância no Evolution API?')) return;
    setWaStatus('loading');
    try {
      await whatsappService.disconnectInstance();
      setWaQrCode(null);
      setWaStatus('disconnected');
      alert('Instância desconectada e apagada no Evolution API com sucesso!');
    } catch (e) {
      alert('Erro ao desconectar instância: ' + (e.response?.data?.error || e.message));
      setWaStatus('disconnected');
    }
  };

  const handleBindInstance = async (name) => {
    const targetName = name || prompt('Digite o nome da instância da Evolution API (ex: CRMPages_5533998420631):');
    if (!targetName) return;
    setWaStatus('loading');
    try {
      const res = await whatsappService.bindInstance(targetName.trim());
      setWaInstanceName(res.data.instanceName);
      setWaStatus('connected');
      alert(`Instância "${res.data.instanceName}" vinculada com sucesso ao Cyber Café!`);
    } catch (e) {
      alert('Erro ao vincular instância: ' + (e.response?.data?.error || e.message));
      setWaStatus('disconnected');
    }
  };

  // Lead & Column Drag and Drop Handlers
  const handleDragStart = (e, leadId) => {
    e.stopPropagation();
    e.dataTransfer.setData('type', 'LEAD');
    e.dataTransfer.setData('leadId', String(leadId));
  };

  const handleColDragStart = (e, colId, index) => {
    // Regra: Primeira (Caixa de Entrada) e última (Venda Ganha) são fixas
    if (index === 0 || index === columns.length - 1) {
      e.preventDefault();
      return;
    }
    e.stopPropagation();
    e.dataTransfer.setData('type', 'COLUMN');
    e.dataTransfer.setData('colId', String(colId));
    e.dataTransfer.setData('colIndex', String(index));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetColId, targetIndex) => {
    e.preventDefault();
    const type = e.dataTransfer.getData('type');

    if (type === 'COLUMN') {
      e.stopPropagation();
      const sourceIndexStr = e.dataTransfer.getData('colIndex');
      if (sourceIndexStr === '' || sourceIndexStr === null || sourceIndexStr === undefined) return;
      const sourceIndex = Number(sourceIndexStr);

      // Regra: Bloqueia reordenar a primeira ou última coluna
      if (sourceIndex === 0 || sourceIndex === columns.length - 1) return;
      if (targetIndex === 0 || targetIndex === columns.length - 1) return;
      if (sourceIndex === targetIndex || isNaN(sourceIndex) || targetIndex === undefined) return;

      const newCols = [...columns];
      const [movedCol] = newCols.splice(sourceIndex, 1);
      newCols.splice(targetIndex, 0, movedCol);

      const updatedCols = newCols.map((c, idx) => ({ ...c, sort_order: idx + 1 }));
      setColumns(updatedCols);

      const payload = updatedCols.map(c => ({ id: c.id, sort_order: c.sort_order }));
      crmSetupService.reorderColumns(payload).catch(err => {
        console.error('Erro ao reordenar colunas', err);
      });
      return;
    }

    // Lead Drop
    const leadId = e.dataTransfer.getData('leadId');
    if (!leadId) return;

    setLeads(current => current.map(l => 
      l.id === Number(leadId) ? { ...l, column_id: targetColId } : l
    ));

    crmLeadService.moveLead(leadId, targetColId).catch(err => {
      alert('Erro ao mover lead');
      crmLeadService.getLeads(activeFunnel).then(r => setLeads(r.data));
    });
  };

  if (loading && columns.length === 0) return <div style={styles.page}>Carregando CRM...</div>;

  const totalFunnelValue = leads.reduce((acc, l) => acc + (Number(l.value) || 0), 0);

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.headerTitle}>
          <h1 style={styles.title}>{isInboxOpen ? '💬 Chatbot / Atendimento' : '📊 CRM / Vendas'}</h1>
          <span style={styles.badge}>{leads.length} leads no funil</span>
          <span style={{ ...styles.badge, background: '#059669', border: '1px solid #10b981', boxShadow: '0 0 10px rgba(16, 185, 129, 0.3)' }}>
            Total: {formatCurrency(totalFunnelValue)}
          </span>
        </div>
        
        <div style={styles.headerActions}>
          <button 
            style={styles.btnSecondary} 
            onClick={() => {
              const nextState = !isInboxOpen;
              setIsInboxOpen(nextState);
              if (!nextState) {
                navigate('/crm');
              } else {
                navigate('/chatbot');
              }
            }}
          >
            {isInboxOpen ? '📊 Ir para Kanban CRM' : '💬 Ir para Chatbot'}
          </button>
          <button style={styles.btnSecondary} onClick={openWhatsAppConfig}>📱 WhatsApp</button>
          <button style={styles.btnSecondary} onClick={() => { setIsTagsModalOpen(true); loadTags(); }}>🏷️ Etiquetas</button>
          <select 
            style={styles.select} 
            value={activeFunnel || ''} 
            onChange={e => setActiveFunnel(Number(e.target.value))}
          >
            {funnels.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <button style={styles.btnPrimary} onClick={() => setIsCreatingLead(true)}>+ Novo Contato</button>
        </div>
      </div>

      {!isInboxOpen ? (
        <div style={styles.kanbanBoard}>
          {columns.map((col, index) => {
            const colLeads = leads.filter(l => l.column_id === col.id);
            const colTotalValue = colLeads.reduce((acc, l) => acc + (Number(l.value) || 0), 0);
            
            return (
              <div 
                key={col.id} 
                style={{ ...styles.column, position: 'relative' }}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, col.id, index)}
              >
                {/* Botão + Redondinho na junção do topo entre colunas (Estilo crmpages) */}
                {index > 0 && (
                  <button
                    style={{
                      position: 'absolute',
                      top: -11,
                      left: -20,
                      zIndex: 25,
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: '#3b82f6',
                      color: '#fff',
                      border: '2px solid #111827',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 15,
                      fontWeight: 'bold',
                      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.5)',
                      padding: 0,
                      lineHeight: 1
                    }}
                    title="Inserir nova coluna aqui"
                    onClick={(e) => { e.stopPropagation(); handleOpenCreateColumn(col.sort_order); }}
                  >
                    +
                  </button>
                )}

                <div 
                  style={{ ...styles.columnHeader, borderTopColor: col.color, cursor: (index === 0 || index === columns.length - 1) ? 'default' : 'grab' }}
                  draggable={index !== 0 && index !== columns.length - 1}
                  onDragStart={(e) => handleColDragStart(e, col.id, index)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {index !== 0 && index !== columns.length - 1 ? (
                      <span 
                        style={{ color: '#9ca3af', fontSize: 16, cursor: 'grab', userSelect: 'none' }} 
                        title="Arrastar para reordenar coluna"
                      >
                        ⋮⋮
                      </span>
                    ) : (
                      <span style={{ fontSize: 10, background: 'rgba(255,255,255,0.1)', color: '#9ca3af', padding: '2px 6px', borderRadius: 4, fontWeight: 'bold' }}>
                        📌 Fixa
                      </span>
                    )}
                    <span style={styles.colName}>{col.name}</span>
                    <button
                      style={styles.pencilBtn}
                      title="Editar nome/cor da coluna"
                      onClick={(e) => { e.stopPropagation(); handleOpenEditColumn(col); }}
                    >
                      ✏️
                    </button>
                    <button
                      style={{ ...styles.pencilBtn, color: '#f59e0b', opacity: 1, fontWeight: 'bold' }}
                      title="Automações e Disparos da Coluna"
                      onClick={(e) => { e.stopPropagation(); handleOpenAutomationModal(col); }}
                    >
                      ⚡
                    </button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, color: '#10b981', fontWeight: 'bold' }}>
                      {formatCurrency(colTotalValue)}
                    </span>
                    <span style={styles.colCount}>{colLeads.length}</span>
                  </div>
                </div>
                
                <div style={styles.leadList}>
                  {colLeads.map(lead => (
                    <div 
                      key={lead.id} 
                      style={{ ...styles.leadCard, position: 'relative' }}
                      draggable
                      onDragStart={(e) => handleDragStart(e, lead.id)}
                      onClick={() => handleSelectLead(lead)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <LeadAvatar lead={lead} size={26} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={styles.leadName}>{lead.client_name || lead.name || lead.phone || 'Sem nome'}</div>
                          <div style={styles.leadPhone}>{lead.client_phone || lead.phone || ''}</div>
                        </div>
                      </div>
                      {Number(lead.value) > 0 && (
                        <div style={{ fontSize: 12, color: '#10b981', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                          💰 {formatCurrency(lead.value)}
                        </div>
                      )}
                      {getLeadTags(lead).length > 0 && (
                        <div style={styles.tagList}>
                          {getLeadTags(lead).map(t => (
                            <span key={t.id} style={{ ...styles.tag, background: t.color || '#3b82f6' }}>{t.name}</span>
                          ))}
                        </div>
                      )}

                      {/* Notificação de Mensagem Não Lida (Circulo Verde) */}
                      {lead.unread_count > 0 && (
                        <span style={{
                          position: 'absolute',
                          bottom: 10,
                          right: 10,
                          background: '#10b981',
                          color: '#fff',
                          borderRadius: 12,
                          padding: '2px 8px',
                          fontSize: 11,
                          fontWeight: 'bold',
                          boxShadow: '0 0 8px rgba(16, 185, 129, 0.6)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4
                        }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />
                          {lead.unread_count}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Card / Botão de Adicionar Nova Coluna no final da lista */}
          <div style={styles.addColumnCard} onClick={() => handleOpenCreateColumn(columns.length)}>
            <span style={{ fontSize: 24, fontWeight: 'bold' }}>+</span>
            <span style={{ fontWeight: '700', fontSize: 13 }}>Nova Coluna</span>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', background: '#111827', gap: 15, paddingBottom: 10 }}>
          {/* Coluna 1: Lista de Contatos */}
          <div style={{ width: 320, display: 'flex', flexDirection: 'column', background: '#1f2937', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ padding: 12, background: '#1f2937', borderBottom: '1px solid #374151' }}>
              <input 
                type="text" 
                placeholder="🔍 Buscar conversas..." 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: 6,
                  background: '#111827',
                  border: '1px solid #10b981',
                  color: '#fff',
                  fontSize: 13,
                  outline: 'none'
                }}
              />
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[...leads]
                .filter(l => {
                  if (!l) return false;
                  const nameStr = String(l.client_name || l.name || '').toLowerCase();
                  const phoneStr = String(l.client_phone || l.phone || '');
                  return nameStr.includes(searchTerm.toLowerCase()) || phoneStr.includes(searchTerm);
                })
                .sort((a, b) => {
                  const dateA = new Date(a?.updated_at || a?.created_at || 0).getTime() || 0;
                  const dateB = new Date(b?.updated_at || b?.created_at || 0).getTime() || 0;
                  return dateB - dateA;
                })
                .map(lead => (
                <div 
                  key={lead.id} 
                  style={{ 
                    padding: 12, borderRadius: 8, cursor: 'pointer', 
                    background: selectedLead?.id === lead.id ? '#064e3b' : '#374151',
                    borderLeft: selectedLead?.id === lead.id ? '4px solid #10b981' : '4px solid transparent',
                    position: 'relative',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => handleSelectLead(lead)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <LeadAvatar lead={lead} size={36} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <strong style={{ color: '#fff', fontSize: 14 }}>{lead.client_name || lead.name || lead.phone || 'Sem nome'}</strong>
                        {lead.unread_count > 0 && (
                          <span style={{
                            background: '#10b981',
                            color: '#fff',
                            borderRadius: 10,
                            padding: '2px 8px',
                            fontSize: 11,
                            fontWeight: 'bold',
                            boxShadow: '0 0 6px rgba(16, 185, 129, 0.5)'
                          }}>
                            {lead.unread_count}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                        <span style={{ color: '#9ca3af', fontSize: 12 }}>{lead.client_phone || lead.phone || ''}</span>
                        <span style={{ color: '#6b7280', fontSize: 11 }}>{safeLeadTime(lead.updated_at || lead.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  {getLeadTags(lead).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                      {getLeadTags(lead).map(t => (
                        <span key={t.id} style={{ background: t.color || '#3b82f6', color: '#fff', padding: '1px 7px', borderRadius: 4, fontSize: 10, fontWeight: 'bold' }}>
                          {t.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Coluna 2: Chat */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#1f2937', borderRadius: 8, overflow: 'hidden' }}>
            {selectedLead ? (
              <>
                <div style={{ padding: 15, background: '#1f2937', borderBottom: '1px solid #374151', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <LeadAvatar lead={selectedLead} size={42} />
                    <div style={{ marginLeft: 12 }}>
                      <h3 style={{ margin: 0, fontSize: 16, color: '#fff' }}>{selectedLead.client_name || selectedLead.name || selectedLead.phone || 'Sem nome'}</h3>
                      <span style={{ fontSize: 12, color: '#9ca3af' }}>{selectedLead.client_phone || selectedLead.phone || ''}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#064e3b', padding: '5px 12px', borderRadius: 12, border: '1px solid #10b981' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981' }} />
                    <span style={{ fontSize: 12, color: '#10b981', fontWeight: '700' }}>
                      CONECTADO · {waInstanceName || 'WhatsApp'}
                    </span>
                  </div>
                </div>
                <div style={{ flex: 1, padding: 20, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, background: '#111827' }}>
                  {(!messages || messages.length === 0) && <div style={styles.mutedText}>Nenhuma mensagem encontrada.</div>}
                  {(Array.isArray(messages) ? messages : []).map((msg, msgIdx) => {
                    if (!msg) return null;
                    const textContent = msg.message_text || msg.content || '';
                    const isPixMsg = textContent.includes('00020126580014br.gov.bcb.pix') || textContent.includes('Chave PIX');

                    let pixCode = '';
                    if (isPixMsg) {
                      const match = textContent.match(/```\n?([\s\S]*?)\n?```/) || textContent.match(/`([\s\S]*?)`/);
                      if (match) pixCode = match[1].trim();
                    }

                    const d = msg.created_at ? new Date(msg.created_at) : new Date();
                    const formattedTime = isNaN(d.getTime()) ? '' : d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

                    return (
                      <div key={msg.id || msgIdx} style={msg.direction === 'outbound' ? styles.messageOut : styles.messageIn}>
                        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.4 }}>
                          {textContent}
                        </div>
                        
                        {pixCode && (
                          <button 
                            onClick={() => {
                              navigator.clipboard.writeText(pixCode);
                              alert('Código PIX copiado com sucesso!');
                            }}
                            style={{
                              marginTop: 8,
                              padding: '6px 12px',
                              borderRadius: 6,
                              background: '#047857',
                              color: '#fff',
                              border: '1px solid #10b981',
                              fontSize: 12,
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6
                            }}
                          >
                            📋 Copiar Código PIX
                          </button>
                        )}

                        {formattedTime && (
                          <div style={styles.messageTime}>
                            {formattedTime}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div ref={chatEndRef} />
                </div>
                <div style={styles.chatInputRow}>
                  <input 
                    type="text" 
                    placeholder="Digite uma mensagem..." 
                    style={styles.chatInput} 
                    value={chatInput}
                    onChange={e => setChatInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSendMessage()}
                  />
                  <button style={styles.sendBtn} onClick={handleSendMessage}>Enviar</button>
                </div>
              </>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>
                Selecione uma conversa ao lado para iniciar o atendimento
              </div>
            )}
          </div>

          {/* Coluna 3: Ficha Completa */}
          <div style={{ width: 320, display: 'flex', flexDirection: 'column', background: '#1f2937', borderRadius: 8, padding: 20, overflowY: 'auto' }}>
            {selectedLead ? (
              <>
                <h3 style={{ ...styles.detailsTitle, marginTop: 0 }}>Ficha Completa</h3>
                
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Nome</label>
                  <input type="text" value={selectedLead.client_name || selectedLead.name || ''} readOnly style={styles.input} />
                </div>
                
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Telefone / WhatsApp</label>
                  <input type="text" value={selectedLead.client_phone || selectedLead.phone || ''} readOnly style={styles.input} />
                </div>

                {/* Campos Personalizados do Lead (Abaixo do Telefone) */}
                <div style={{ marginTop: 12, marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <label style={{ ...styles.label, color: '#60a5fa', fontWeight: 'bold', margin: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                      ⚙️ Campos Personalizados
                    </label>
                    <button
                      onClick={handleOpenCreateCustomField}
                      title="Adicionar ou gerenciar campos personalizados do Lead"
                      style={{ border: 0, background: 'none', color: '#60a5fa', cursor: 'pointer', fontSize: 11, fontWeight: 'bold' }}
                    >
                      + Adicionar Campo
                    </button>
                  </div>

                  {customFields.length === 0 ? (
                    <div style={{ color: '#6b7280', fontSize: 11, fontStyle: 'italic', background: '#111827', padding: '8px 10px', borderRadius: 6, textAlign: 'center', border: '1px dashed #374151' }}>
                      Nenhum campo personalizado. <span onClick={handleOpenCreateCustomField} style={{ color: '#3b82f6', cursor: 'pointer', textDecoration: 'underline' }}>+ Criar agora</span>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {customFields.map(cf => {
                        const fieldWidth = cf.width === 'third' ? 'calc(33.33% - 6px)' : cf.width === 'half' ? 'calc(50% - 4px)' : '100%';
                        const val = leadCustomValues[cf.id] !== undefined ? leadCustomValues[cf.id] : '';

                        return (
                          <div key={cf.id} style={{ width: fieldWidth, display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                              <span style={{ fontSize: 11, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={cf.name}>
                                {cf.name}
                              </span>
                              <button
                                onClick={() => handleOpenEditCustomField(cf)}
                                title="Editar definição deste campo"
                                style={{ border: 0, background: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 11, padding: 0, marginLeft: 2 }}
                              >
                                ✏️
                              </button>
                            </div>

                            {cf.field_type === 'select' ? (
                              <select
                                style={{ ...styles.input, padding: '6px 8px', fontSize: 12, height: 32 }}
                                value={val}
                                onChange={(e) => handleSaveCustomFieldValue(cf.id, e.target.value)}
                              >
                                <option value="">Selecione...</option>
                                {(cf.options || '').split(',').map((opt, i) => (
                                  <option key={i} value={opt.trim()}>{opt.trim()}</option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type={cf.field_type === 'number' ? 'number' : cf.field_type === 'date' ? 'date' : 'text'}
                                placeholder={cf.name}
                                style={{ ...styles.input, padding: '6px 8px', fontSize: 12, height: 32 }}
                                value={val}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setLeadCustomValues(prev => ({ ...prev, [cf.id]: v }));
                                }}
                                onBlur={(e) => handleSaveCustomFieldValue(cf.id, e.target.value)}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div style={styles.fieldGroup}>
                  <label style={{ ...styles.label, color: '#10b981', fontWeight: 'bold' }}>💰 Valor da Oportunidade (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    style={{ ...styles.input, borderColor: '#10b981', fontWeight: 'bold', color: '#10b981', fontSize: 15 }}
                    value={selectedLead.value !== undefined && selectedLead.value !== null ? selectedLead.value : ''}
                    onChange={(e) => {
                      const newVal = e.target.value;
                      setSelectedLead({ ...selectedLead, value: newVal });
                    }}
                    onBlur={async () => {
                      try {
                        const valNum = parseFloat(selectedLead.value) || 0;
                        await crmLeadService.updateLead(selectedLead.id, { value: valNum });
                        setLeads(prev => prev.map(l => l.id === selectedLead.id ? { ...l, value: valNum } : l));
                      } catch (e) {
                        console.error('Erro ao atualizar valor do lead', e);
                      }
                    }}
                  />
                </div>

                {/* Orçamentos / Venda de Pacotes Unificado */}
                <div style={{
                  background: '#111827',
                  padding: 12,
                  borderRadius: 10,
                  border: '1px solid #374151',
                  marginTop: 14,
                  marginBottom: 14
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <label style={{ color: '#10b981', fontWeight: 'bold', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
                      🎟️ Orçamentos
                    </label>
                    <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 'bold' }}>
                      {getLeadTags(selectedLead).some(t => t.name === 'Proposta Aceita') ? 'Venda Ganha' : getLeadTags(selectedLead).some(t => t.name === 'Proposta Pendente') ? 'Pendente' : 'Sem Proposta'}
                    </span>
                  </div>

                  {getLeadTags(selectedLead).some(t => t.name === 'Proposta Aceita') ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ background: '#064e3b', color: '#10b981', padding: '8px 10px', borderRadius: 8, fontSize: 12, fontWeight: 'bold', border: '1px solid #10b981', textAlign: 'center', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ whiteSpace: 'nowrap' }}>✅ Venda Ganha</span>
                        <span style={{ fontSize: 13, color: '#fff', whiteSpace: 'nowrap' }}>R$ {Number(selectedLead.value || 0).toFixed(2)}</span>
                      </div>

                      {/* Vincular/Cadastrar Cliente */}
                      <div style={{ background: '#1f2937', padding: 8, borderRadius: 6, border: '1px solid #374151' }}>
                        {selectedLead.client_id ? (
                          <div style={{ color: '#10b981', fontSize: 11, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <span>👤 Cliente:</span>
                            <span style={{ color: '#fff' }}>{selectedLead.client_name || selectedLead.name}</span>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <div style={{ color: '#fbbf24', fontSize: 11 }}>
                              💡 Cadastre como Cliente para liberar horas na arena:
                            </div>
                            <button
                              onClick={handleOpenConvertModal}
                              style={{ width: '100%', background: '#3b82f6', color: '#fff', border: 0, padding: '7px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, whiteSpace: 'nowrap' }}
                            >
                              ➕ Cadastrar como Cliente
                            </button>
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={openProposalModal}
                          style={{ flex: 1, background: '#374151', color: '#fff', border: '1px solid #4b5563', padding: '7px 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' }}
                        >
                          ✏️ Novo Orçamento
                        </button>
                        <button
                          onClick={() => handleToggleProposalStatus('pending')}
                          style={{ flex: 1, background: '#374151', color: '#9ca3af', border: '1px solid #4b5563', padding: '7px 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          🔄 Tornar Pendente
                        </button>
                      </div>
                    </div>
                  ) : getLeadTags(selectedLead).some(t => t.name === 'Proposta Pendente') ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ background: '#78350f', color: '#f59e0b', padding: '8px 10px', borderRadius: 8, fontSize: 12, fontWeight: 'bold', border: '1px solid #f59e0b', textAlign: 'center', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ whiteSpace: 'nowrap' }}>⏳ Pagamento Pendente</span>
                        <span style={{ fontSize: 13, color: '#fff', whiteSpace: 'nowrap' }}>R$ {Number(selectedLead.value || 0).toFixed(2)}</span>
                      </div>

                      <button
                        onClick={() => handleToggleProposalStatus('accepted')}
                        style={{ width: '100%', background: '#047857', color: '#fff', border: '1px solid #10b981', padding: '9px 10px', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontWeight: 'bold', boxShadow: '0 2px 8px rgba(16, 185, 129, 0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, whiteSpace: 'nowrap' }}
                      >
                        ✅ Confirmar Pagamento
                      </button>

                      <button
                        onClick={openProposalModal}
                        style={{ width: '100%', background: '#1f2937', color: '#60a5fa', border: '1px solid #3b82f6', padding: '8px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' }}
                      >
                        ✏️ Alterar Proposta
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <button
                        onClick={openProposalModal}
                        style={{ width: '100%', background: '#10b981', color: '#fff', border: 0, padding: '9px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontWeight: 'bold', boxShadow: '0 2px 10px rgba(16, 185, 129, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, whiteSpace: 'nowrap' }}
                      >
                        🎟️ Criar Orçamento / Vender
                      </button>
                    </div>
                  )}
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Funil / Etapa Atual</label>
                  <select style={styles.input} value={selectedLead.column_id} onChange={(e) => {
                    handleDrop({preventDefault:()=>{}, dataTransfer:{getData:()=>selectedLead.id}}, Number(e.target.value));
                    setSelectedLead({...selectedLead, column_id: Number(e.target.value)});
                  }}>
                    {columns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>

                {/* Etiquetas do Lead */}
                <div style={{ ...styles.fieldGroup, marginTop: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <label style={styles.label}>Etiquetas do Lead</label>
                    <button
                      onClick={() => { setIsTagsModalOpen(true); loadTags(); }}
                      style={{ border: 0, background: 'none', color: '#10b981', cursor: 'pointer', fontSize: 11, fontWeight: 'bold' }}
                    >
                      + Gerenciar
                    </button>
                  </div>
                  
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                    {getLeadTags(selectedLead).length === 0 ? (
                      <span style={{ color: '#6b7280', fontSize: 12, fontStyle: 'italic' }}>Nenhuma etiqueta atribuída</span>
                    ) : (
                      getLeadTags(selectedLead).map(t => (
                        <span
                          key={t.id}
                          style={{
                            background: t.color || '#3b82f6',
                            color: '#fff',
                            borderRadius: 12,
                            padding: '3px 10px',
                            fontSize: 11,
                            fontWeight: 'bold',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6
                          }}
                        >
                          {t.name}
                          <button
                            onClick={() => handleRemoveTagFromLead(selectedLead.id, t.id)}
                            title="Remover etiqueta"
                            style={{ border: 0, background: 'rgba(0,0,0,0.2)', borderRadius: '50%', width: 14, height: 14, color: '#fff', cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          >
                            ×
                          </button>
                        </span>
                      ))
                    )}
                  </div>

                  <select
                    style={{ ...styles.input, fontSize: 12 }}
                    value=""
                    onChange={e => {
                      if (e.target.value) handleAddTagToLead(selectedLead.id, Number(e.target.value));
                    }}
                  >
                    <option value="">Buscar / Atribuir etiqueta...</option>
                    {(Array.isArray(tagsList) ? tagsList : [])
                      .filter(t => !getLeadTags(selectedLead).some(lt => Number(lt.id) === Number(t.id)))
                      .map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))
                    }
                  </select>
                </div>

                <div style={{ ...styles.actions, flexDirection: 'column', marginTop: 16 }}>
                  <button style={{ ...styles.secondaryAction, width: '100%' }}>📅 Vincular à Agenda</button>
                </div>

                {/* Notas de Atendimento (interno) */}
                <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #374151' }}>
                  <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#f3f4f6', fontWeight: 'bold', marginBottom: 10 }}>
                    📄 Notas de Atendimento (interno)
                  </label>
                  
                  <textarea
                    placeholder="Escreva uma nota interna (não visível ao cliente)..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 8,
                      background: '#111827',
                      border: '1px solid #374151',
                      color: '#fff',
                      fontSize: 13,
                      resize: 'vertical',
                      boxSizing: 'border-box',
                      outline: 'none',
                      fontFamily: 'inherit',
                      marginBottom: 10
                    }}
                    value={newNoteContent}
                    onChange={e => setNewNoteContent(e.target.value)}
                  />

                  <button
                    onClick={handleAddNote}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: 8,
                      background: '#3b82f6',
                      color: '#fff',
                      border: 'none',
                      fontWeight: 'bold',
                      fontSize: 13,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                      marginBottom: 16,
                      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                    }}
                  >
                    + Adicionar Nota
                  </button>

                  {/* Histórico de Notas */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 260, overflowY: 'auto' }}>
                    {(!leadNotes || leadNotes.length === 0) ? (
                      <div style={{ color: '#6b7280', fontSize: 12, fontStyle: 'italic', textAlign: 'center', padding: '10px 0' }}>
                        Nenhuma nota ainda.
                      </div>
                    ) : (
                      (Array.isArray(leadNotes) ? leadNotes : []).map(n => {
                        if (!n) return null;
                        const nd = n.created_at ? new Date(n.created_at) : new Date();
                        const dateStr = isNaN(nd.getTime()) ? '' : `${nd.toLocaleDateString('pt-BR')} ${nd.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;

                        return (
                          <div
                            key={n.id}
                            style={{
                              background: '#111827',
                              border: '1px solid #374151',
                              borderRadius: 8,
                              padding: '10px 12px',
                              fontSize: 12,
                              position: 'relative'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                              <span style={{ color: '#60a5fa', fontWeight: 'bold', fontSize: 11 }}>{n.author_name || 'Atendente'}</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ color: '#6b7280', fontSize: 10 }}>
                                  {dateStr}
                                </span>
                                <button
                                  onClick={() => handleDeleteNote(n.id)}
                                  title="Excluir nota"
                                  style={{ border: 0, background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12, padding: 0 }}
                                >
                                  🗑️
                                </button>
                              </div>
                            </div>
                            <div style={{ color: '#e5e7eb', whiteSpace: 'pre-wrap', lineHeight: 1.4, wordBreak: 'break-word' }}>
                              {n.content}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280', textAlign: 'center' }}>
                Detalhes do lead e ferramentas aparecerão aqui
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* NOVO LEAD MODAL */}
      {isCreatingLead && (
        <div style={styles.modalOverlay} onClick={() => setIsCreatingLead(false)}>
          <div style={{ ...styles.modal, width: 400 }} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Novo Contato (Lead)</h3>
            <form onSubmit={handleCreateLead} style={{ padding: 20 }}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Nome ou Apelido</label>
                <input 
                  type="text" 
                  style={styles.input}
                  value={newLeadForm.name}
                  onChange={e => setNewLeadForm({...newLeadForm, name: e.target.value})}
                  autoFocus
                />
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>WhatsApp</label>
                <input 
                  type="text" 
                  style={styles.input}
                  placeholder="Ex: 5531999999999"
                  value={newLeadForm.phone}
                  onChange={e => setNewLeadForm({...newLeadForm, phone: e.target.value})}
                />
              </div>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Valor da Oportunidade (R$)</label>
                <input 
                  type="number"
                  step="0.01" 
                  style={styles.input}
                  placeholder="Ex: 150.00"
                  value={newLeadForm.value || ''}
                  onChange={e => setNewLeadForm({...newLeadForm, value: e.target.value})}
                />
              </div>
              <div style={styles.modalActions}>
                <button type="button" style={styles.secondaryAction} onClick={() => setIsCreatingLead(false)}>Cancelar</button>
                <button type="submit" style={styles.primaryAction}>Criar Lead</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* WHATSAPP CONFIG MODAL */}
      {isWhatsAppConfigOpen && (
        <div style={styles.modalOverlay} onClick={() => setIsWhatsAppConfigOpen(false)}>
          <div style={{ ...styles.modal, width: 450, height: 'auto', minHeight: 300, textAlign: 'center', padding: '30px 20px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ ...styles.modalTitle, margin: '0 0 20px 0', fontSize: 22 }}>Configuração WhatsApp</h3>
            
            {waStatus === 'loading' && (
              <div style={{ padding: 40 }}>
                <p style={{ color: '#9ca3af', fontSize: 16 }}>Carregando status...</p>
              </div>
            )}
            
            {waStatus === 'connected' && (
              <div style={{ padding: '20px 10px' }}>
                <div style={{ fontSize: 50, marginBottom: 10 }}>✅</div>

                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#064e3b', border: '1px solid #10b981', color: '#10b981', padding: '6px 14px', borderRadius: 20, fontWeight: '900', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981' }} />
                  CONECTADO
                </div>

                <div style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 10, padding: '14px 18px', margin: '0 0 20px 0', textAlign: 'left' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: waOwnerPhone ? 8 : 0 }}>
                    <span style={{ color: '#9ca3af', fontSize: 13 }}>Instância:</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <strong style={{ color: '#38bdf8', fontSize: 14, fontFamily: 'monospace' }}>{waInstanceName || 'tenant_1'}</strong>
                      <button
                        style={{ border: '1px solid #3b82f6', background: 'transparent', color: '#60a5fa', borderRadius: 6, padding: '2px 8px', fontSize: 11, cursor: 'pointer', fontWeight: 'bold' }}
                        onClick={() => handleBindInstance()}
                        title="Trocar ou Vincular nome de Instância do WhatsApp"
                      >
                        ✏️ Trocar Nome
                      </button>
                    </div>
                  </div>
                  {waOwnerPhone && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#9ca3af', fontSize: 13 }}>Telefone Vinculado:</span>
                      <strong style={{ color: '#a7f3d0', fontSize: 14 }}>+{waOwnerPhone}</strong>
                    </div>
                  )}
                </div>

                <p style={{ color: '#9ca3af', fontSize: 14, lineHeight: 1.5, marginBottom: 22 }}>
                  Seu número está ativo e pronto para enviar e receber mensagens direto do CRM.
                </p>

                <button
                  style={{ padding: '11px 22px', borderRadius: 8, background: '#ef4444', color: '#fff', border: 'none', fontWeight: 'bold', fontSize: 14, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, boxShadow: '0 4px 14px rgba(239, 68, 68, 0.35)' }}
                  onClick={handleDisconnectQr}
                >
                  🗑️ Desconectar Instância (Apagar no Evolution)
                </button>
              </div>
            )}
            
            {waStatus === 'disconnected' && (
              <div style={{ padding: '10px 20px' }}>
                {!waQrCode ? (
                  <>
                    <div style={{ fontSize: 50, marginBottom: 15 }}>📱</div>
                    <p style={{ color: '#9ca3af', fontSize: 15, marginBottom: 25, lineHeight: 1.5 }}>Você precisa ler o QR Code para conectar seu número e liberar o Chat.</p>
                    <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <button style={{ ...styles.btnPrimary, fontSize: 15, padding: '10px 24px' }} onClick={handleGenerateQr}>Gerar QR Code</button>
                      <button
                        style={{ padding: '10px 18px', borderRadius: 8, background: '#dc2626', color: '#fff', border: 'none', fontWeight: 'bold', fontSize: 14, cursor: 'pointer' }}
                        onClick={handleDisconnectQr}
                      >
                        🗑️ Desconectar Instância
                      </button>
                    </div>

                    <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #2d3748', display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
                      <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>Ou troque / vincule o Nome da Instância ativada no Evolution API:</p>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                        <button
                          style={{ padding: '9px 18px', borderRadius: 8, background: '#1d4ed8', color: '#fff', border: 'none', fontWeight: 'bold', fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                          onClick={() => handleBindInstance('CRMPages_5533998420631')}
                        >
                          🔗 Vincular Arena (CRMPages_5533998420631)
                        </button>
                        <button
                          style={{ padding: '9px 18px', borderRadius: 8, background: '#374151', color: '#fff', border: '1px solid #4b5563', fontWeight: 'bold', fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                          onClick={() => handleBindInstance()}
                        >
                          ✏️ Trocar Nome da Instância
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <h4 style={{ color: '#fff', fontSize: 18, margin: '0 0 15px 0' }}>Escaneie o QR Code</h4>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 15 }}>
                      <img src={waQrCode} alt="WhatsApp QR Code" style={{ width: 260, height: 260, borderRadius: 12, background: '#fff', padding: 15 }} />
                    </div>
                    <p style={{ color: '#fbbf24', fontSize: 14, margin: '10px 0 20px 0', lineHeight: 1.5 }}>Abra o WhatsApp no celular, vá em "Aparelhos Conectados" e escaneie a imagem acima.<br/><span style={{ color: '#9ca3af', fontSize: 12 }}>(A tela atualizará automaticamente)</span></p>
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <button style={styles.secondaryAction} onClick={openWhatsAppConfig}>Atualizar Manualmente</button>
                      <button
                        style={{ padding: '8px 16px', borderRadius: 6, background: '#ef4444', color: '#fff', border: 'none', fontWeight: 'bold', fontSize: 13, cursor: 'pointer' }}
                        onClick={handleDisconnectQr}
                      >
                        🗑️ Desconectar / Apagar Instância
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
            
            <div style={{ marginTop: 'auto', paddingTop: 20, textAlign: 'right' }}>
              <button style={styles.secondaryAction} onClick={() => setIsWhatsAppConfigOpen(false)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* GERENCIAR ETIQUETAS MODAL */}
      {isTagsModalOpen && (
        <div style={styles.modalOverlay} onClick={() => setIsTagsModalOpen(false)}>
          <div style={{ ...styles.modal, width: 500, height: 'auto', maxHeight: '85vh', padding: 24, background: '#111827', borderRadius: 14, border: '1px solid #374151' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
                🏷️ Gerenciar Etiquetas (Tags)
              </h3>
              <button style={styles.closeBtn} onClick={() => setIsTagsModalOpen(false)}>×</button>
            </div>

            {/* Formulário de Nova Etiqueta */}
            <form onSubmit={handleCreateTag} style={{ background: '#1f2937', padding: 16, borderRadius: 10, border: '1px solid #374151', marginBottom: 20 }}>
              <label style={{ ...styles.label, color: '#f3f4f6', fontWeight: 'bold' }}>Criar Nova Etiqueta</label>
              <input
                type="text"
                placeholder="Nome da etiqueta (ex: ANÚNCIO, ANIVERSÁRIO, VIP...)"
                style={{ ...styles.input, marginBottom: 12 }}
                value={newTagName}
                onChange={e => setNewTagName(e.target.value)}
              />

              <label style={{ ...styles.label, marginBottom: 8 }}>Selecione a Cor do Background</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14, alignItems: 'center' }}>
                {presetColors.map(c => (
                  <button
                    type="button"
                    key={c}
                    onClick={() => setNewTagColor(c)}
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: c,
                      border: newTagColor === c ? '3px solid #fff' : '2px solid transparent',
                      cursor: 'pointer',
                      boxShadow: newTagColor === c ? '0 0 8px ' + c : 'none'
                    }}
                  />
                ))}
                <input
                  type="color"
                  value={newTagColor}
                  onChange={e => setNewTagColor(e.target.value)}
                  style={{ width: 32, height: 32, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
                />
              </div>

              {/* Prévia */}
              {newTagName && (
                <div style={{ marginBottom: 14 }}>
                  <span style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 4 }}>Prévia visual:</span>
                  <span style={{ background: newTagColor, color: '#fff', padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 'bold' }}>
                    {newTagName}
                  </span>
                </div>
              )}

              <button type="submit" style={{ ...styles.primaryAction, width: '100%', padding: '10px' }}>
                + Criar Etiqueta
              </button>
            </form>

            {/* Lista de Etiquetas Cadastradas */}
            <label style={{ ...styles.label, color: '#f3f4f6', fontWeight: 'bold', marginBottom: 10, display: 'block' }}>
              Etiquetas Cadastradas ({tagsList.length})
            </label>
            <div style={{ maxHeight: 240, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: 10, padding: 4 }}>
              {tagsList.length === 0 ? (
                <span style={{ color: '#6b7280', fontSize: 13, fontStyle: 'italic' }}>Nenhuma etiqueta cadastrada ainda.</span>
              ) : (
                tagsList.map(t => (
                  <div
                    key={t.id}
                    style={{
                      background: t.color || '#3b82f6',
                      color: '#fff',
                      borderRadius: 14,
                      padding: '6px 14px',
                      fontSize: 12,
                      fontWeight: 'bold',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                    }}
                  >
                    <span>{t.name}</span>
                    <button
                      onClick={() => handleDeleteTag(t.id)}
                      title="Apagar etiqueta"
                      style={{ border: 0, background: 'rgba(0,0,0,0.2)', color: '#fff', borderRadius: '50%', width: 18, height: 18, cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      ✕
                    </button>
                  </div>
                ))
              )}
            </div>

            <div style={{ marginTop: 20, textAlign: 'right' }}>
              <button style={styles.secondaryAction} onClick={() => setIsTagsModalOpen(false)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* PROPOSTA DINÂMICA MODAL */}
      {isProposalOpen && (
        <div style={styles.modalOverlay} onClick={() => setIsProposalOpen(false)}>
          <div style={{ ...styles.modal, width: 560, height: 'auto', maxHeight: '90vh', padding: 0, background: '#18181b', borderRadius: 16, border: '1px solid #27272a', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #27272a' }}>
              <h3 style={{ margin: 0, fontSize: 18, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#3b82f6' }}>✨</span> Proposta Dinâmica
              </h3>
              <button style={{ background: '#3f3f46', color: '#fff', border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer' }} onClick={() => setIsProposalOpen(false)}>✕</button>
            </div>

            {/* Tabs */}
            <div style={{ padding: '15px 20px 0 20px' }}>
              <div style={{ display: 'flex', background: '#27272a', borderRadius: 8, padding: 4 }}>
                <button 
                  style={{ flex: 1, padding: '8px 12px', border: 'none', borderRadius: 6, background: proposalTab === 'products' ? '#18181b' : 'transparent', color: proposalTab === 'products' ? '#fff' : '#a1a1aa', fontWeight: '600', cursor: 'pointer' }}
                  onClick={() => setProposalTab('products')}
                >
                  Por produtos
                </button>
                <button 
                  style={{ flex: 1, padding: '8px 12px', border: 'none', borderRadius: 6, background: proposalTab === 'custom' ? '#18181b' : 'transparent', color: proposalTab === 'custom' ? '#fff' : '#a1a1aa', fontWeight: '600', cursor: 'pointer' }}
                  onClick={() => setProposalTab('custom')}
                >
                  Avulsa (link de pagamento)
                </button>
              </div>
            </div>

            {/* Content */}
            <div style={{ padding: 20, maxHeight: 320, overflowY: 'auto' }}>
              {proposalTab === 'products' ? (
                <div>
                  <p style={{ color: '#a1a1aa', fontSize: 13, margin: '0 0 12px 0' }}>Selecione os produtos que mais se encaixam no perfil do cliente.</p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {catalogItems.length === 0 ? (
                      <div style={{ color: '#71717a', textAlign: 'center', padding: 20 }}>Carregando pacotes e produtos disponíveis...</div>
                    ) : (
                      catalogItems.map(item => {
                        const isChecked = !!selectedItems[item.id];
                        const qty = selectedItems[item.id]?.qty || 1;

                        return (
                          <div 
                            key={item.id} 
                            style={{ 
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
                              padding: 12, borderRadius: 10, background: isChecked ? '#27272a' : '#1f1f23', 
                              border: isChecked ? '1px solid #3b82f6' : '1px solid #27272a'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, paddingRight: 10 }}>
                              <input 
                                type="checkbox" 
                                checked={isChecked} 
                                onChange={() => handleToggleItem(item)}
                                style={{ width: 18, height: 18, accentColor: '#2563eb', cursor: 'pointer' }}
                              />
                              <div>
                                <strong style={{ color: '#fff', fontSize: 14, display: 'block' }}>{item.name}</strong>
                                {item.description && <span style={{ color: '#a1a1aa', fontSize: 12 }}>{item.description}</span>}
                              </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              {isChecked && (
                                <div style={{ display: 'flex', alignItems: 'center', background: '#18181b', borderRadius: 6, border: '1px solid #3f3f46' }}>
                                  <button style={{ padding: '2px 8px', border: 'none', background: 'transparent', color: '#fff', cursor: 'pointer' }} onClick={() => handleQtyChange(item.id, -1)}>-</button>
                                  <span style={{ padding: '2px 8px', color: '#fff', fontSize: 13, fontWeight: 'bold' }}>{qty}</span>
                                  <button style={{ padding: '2px 8px', border: 'none', background: 'transparent', color: '#fff', cursor: 'pointer' }} onClick={() => handleQtyChange(item.id, 1)}>+</button>
                                </div>
                              )}
                              <strong style={{ color: '#fff', fontSize: 14, minWidth: 80, textAlign: 'right' }}>
                                R$ {(item.price * qty).toFixed(2)}
                              </strong>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                  <div>
                    <label style={{ color: '#a1a1aa', fontSize: 13, display: 'block', marginBottom: 6 }}>Descrição da Cobrança / Serviço</label>
                    <input 
                      type="text" 
                      placeholder="Ex: Reserva Completa de Aniversário" 
                      value={customDescription} 
                      onChange={e => setCustomDescription(e.target.value)}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: 8, background: '#27272a', border: '1px solid #3f3f46', color: '#fff', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ color: '#a1a1aa', fontSize: 13, display: 'block', marginBottom: 6 }}>Valor da Cobrança (R$)</label>
                    <input 
                      type="number" 
                      placeholder="0.00" 
                      value={customAmount} 
                      onChange={e => setCustomAmount(e.target.value)}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: 8, background: '#27272a', border: '1px solid #3f3f46', color: '#fff', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Summary Footer */}
            {proposalTab === 'products' && (
              <div style={{ padding: '12px 20px', background: '#27272a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#a1a1aa', fontSize: 14 }}>{Object.keys(selectedItems).length} item(ns) selecionado(s)</span>
                <strong style={{ color: '#fff', fontSize: 18 }}>
                  R$ {Object.values(selectedItems).reduce((sum, i) => sum + (i.price * i.qty), 0).toFixed(2)}
                </strong>
              </div>
            )}

            {/* Payment Options */}
            <div style={{ padding: 20, borderTop: '1px solid #27272a', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input 
                  type="checkbox" 
                  checked={includePayment} 
                  onChange={e => setIncludePayment(e.target.checked)}
                  style={{ width: 18, height: 18, accentColor: '#2563eb' }}
                />
                <div>
                  <strong style={{ color: '#fff', fontSize: 14, display: 'block' }}>Incluir pagamento?</strong>
                  <span style={{ color: '#a1a1aa', fontSize: 12 }}>Gera o link / chave PIX de cobrança do Mercado Pago ao aceitar</span>
                </div>
              </label>

              {includePayment && (
                <div>
                  <label style={{ color: '#a1a1aa', fontSize: 12, display: 'block', marginBottom: 6, fontWeight: 'bold' }}>
                    💳 Forma de Recebimento do Mercado Pago
                  </label>
                  <select 
                    value={paymentMethod} 
                    onChange={e => setPaymentMethod(e.target.value)}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, background: '#27272a', border: '1px solid #3f3f46', color: '#fff', boxSizing: 'border-box', fontSize: 13, fontWeight: 'bold' }}
                  >
                    <option value="pix">⚡ Código PIX Copia e Cola (Pagamento Instantâneo)</option>
                    <option value="link">💳 Link de Checkout Mercado Pago (Cartão de Crédito / Débito / PIX)</option>
                  </select>
                </div>
              )}

              <button 
                onClick={handleSendProposal} 
                disabled={isSendingProposal}
                style={{ width: '100%', padding: 14, borderRadius: 10, background: '#2563eb', color: '#fff', border: 'none', fontWeight: 'bold', fontSize: 15, cursor: 'pointer', marginTop: 10, opacity: isSendingProposal ? 0.7 : 1 }}
              >
                {isSendingProposal ? 'Gerando e Enviando...' : 'Gerar Proposta com Pagamento'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADICIONAR / EDITAR COLUNA MODAL */}
      {isColumnModalOpen && (
        <div style={styles.modalOverlay} onClick={() => setIsColumnModalOpen(false)}>
          <div style={{ ...styles.modal, width: 420, height: 'auto', padding: 24, background: '#111827', borderRadius: 14, border: '1px solid #374151' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: 18, fontWeight: 'bold' }}>
                {editingColumn ? '✏️ Editar Coluna' : '➕ Nova Coluna (Etapa)'}
              </h3>
              <button style={styles.closeBtn} onClick={() => setIsColumnModalOpen(false)}>×</button>
            </div>

            <form onSubmit={handleSaveColumn}>
              <div style={styles.fieldGroup}>
                <label style={{ ...styles.label, color: '#f3f4f6', fontWeight: 'bold' }}>Nome da Coluna / Etapa</label>
                <input
                  type="text"
                  placeholder="Ex: Qualificação, Proposta, Agendado, Vendido..."
                  style={styles.input}
                  value={colForm.name}
                  onChange={e => setColForm({ ...colForm, name: e.target.value })}
                  autoFocus
                />
              </div>

              <div style={styles.fieldGroup}>
                <label style={{ ...styles.label, color: '#f3f4f6', fontWeight: 'bold' }}>Cor de Destaque / Header</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8, marginBottom: 10, alignItems: 'center' }}>
                  {presetColors.map(c => (
                    <button
                      type="button"
                      key={c}
                      onClick={() => setColForm({ ...colForm, color: c })}
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: c,
                        border: colForm.color === c ? '3px solid #fff' : '2px solid transparent',
                        cursor: 'pointer',
                        boxShadow: colForm.color === c ? '0 0 8px ' + c : 'none'
                      }}
                    />
                  ))}
                  <input
                    type="color"
                    value={colForm.color}
                    onChange={e => setColForm({ ...colForm, color: e.target.value })}
                    style={{ width: 32, height: 32, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24 }}>
                {editingColumn ? (
                  <button
                    type="button"
                    onClick={handleDeleteColumn}
                    style={{ border: 0, background: '#ef4444', color: '#fff', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', fontWeight: 'bold', fontSize: 13 }}
                  >
                    🗑️ Excluir Coluna
                  </button>
                ) : <div />}

                <div style={{ display: 'flex', gap: 10 }}>
                  <button type="button" style={styles.secondaryAction} onClick={() => setIsColumnModalOpen(false)}>
                    Cancelar
                  </button>
                  <button type="submit" style={styles.primaryAction}>
                    Salvar Coluna
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ADICIONAR / EDITAR CAMPO PERSONALIZADO MODAL */}
      {isCustomFieldModalOpen && (
        <div style={styles.modalOverlay} onClick={() => setIsCustomFieldModalOpen(false)}>
          <div style={{ ...styles.modal, width: 440, height: 'auto', padding: 24, background: '#111827', borderRadius: 14, border: '1px solid #374151' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: 18, fontWeight: 'bold' }}>
                {editingCustomField ? '✏️ Editar Campo Personalizado' : '➕ Criar Campo Personalizado do Lead'}
              </h3>
              <button style={styles.closeBtn} onClick={() => setIsCustomFieldModalOpen(false)}>×</button>
            </div>

            <form onSubmit={handleSaveCustomFieldDefinition} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ ...styles.label, marginBottom: 6 }}>Nome do Campo</label>
                <input
                  type="text"
                  placeholder="Ex: Origem do Cliente, Empresa, CPF, Aniversário..."
                  value={customFieldForm.name}
                  onChange={e => setCustomFieldForm({ ...customFieldForm, name: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, background: '#1f2937', border: '1px solid #374151', color: '#fff', fontSize: 13, boxSizing: 'border-box' }}
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ ...styles.label, marginBottom: 6 }}>Tipo de Dado</label>
                  <select
                    value={customFieldForm.field_type}
                    onChange={e => setCustomFieldForm({ ...customFieldForm, field_type: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, background: '#1f2937', border: '1px solid #374151', color: '#fff', fontSize: 13, boxSizing: 'border-box' }}
                  >
                    <option value="text">🔤 Texto Livre</option>
                    <option value="number">🔢 Número</option>
                    <option value="date">📅 Data</option>
                    <option value="select">📋 Seleção / Lista</option>
                  </select>
                </div>

                <div style={{ flex: 1 }}>
                  <label style={{ ...styles.label, marginBottom: 6 }}>Tamanho na Ficha</label>
                  <select
                    value={customFieldForm.width}
                    onChange={e => setCustomFieldForm({ ...customFieldForm, width: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, background: '#1f2937', border: '1px solid #374151', color: '#fff', fontSize: 13, boxSizing: 'border-box' }}
                  >
                    <option value="third">Pequeno (1/3 de coluna)</option>
                    <option value="half">Médio (1/2 de coluna)</option>
                    <option value="full">Grande (1/1 coluna cheia)</option>
                  </select>
                </div>
              </div>

              {customFieldForm.field_type === 'select' && (
                <div>
                  <label style={{ ...styles.label, marginBottom: 6 }}>Opções da Lista (separadas por vírgula)</label>
                  <input
                    type="text"
                    placeholder="Ex: Instagram, Indicação, Google, Facebook"
                    value={customFieldForm.options}
                    onChange={e => setCustomFieldForm({ ...customFieldForm, options: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, background: '#1f2937', border: '1px solid #374151', color: '#fff', fontSize: 13, boxSizing: 'border-box' }}
                  />
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 10 }}>
                {editingCustomField ? (
                  <button
                    type="button"
                    onClick={() => handleDeleteCustomField(editingCustomField.id)}
                    style={{ padding: '10px 14px', background: '#991b1b', color: '#fff', border: 0, borderRadius: 8, cursor: 'pointer', fontWeight: 'bold', fontSize: 12 }}
                  >
                    🗑️ Excluir Campo
                  </button>
                ) : <div />}

                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    type="button"
                    onClick={() => setIsCustomFieldModalOpen(false)}
                    style={{ padding: '10px 16px', background: '#374151', color: '#fff', border: 0, borderRadius: 8, cursor: 'pointer', fontWeight: 'bold', fontSize: 13 }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    style={{ padding: '10px 20px', background: '#3b82f6', color: '#fff', border: 0, borderRadius: 8, cursor: 'pointer', fontWeight: 'bold', fontSize: 13 }}
                  >
                    {editingCustomField ? 'Salvar Alterações' : 'Criar Campo'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Converter Lead em Cliente */}
      {isConvertModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setIsConvertModalOpen(false)}>
          <div style={{ background: '#14141f', border: '1px solid #374151', borderRadius: 12, padding: 24, width: 400, maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: 17, fontWeight: 'bold' }}>👤 Converter Lead em Cliente</h3>
              <button type="button" style={{ background: 'none', border: 0, color: '#9ca3af', fontSize: 20, cursor: 'pointer' }} onClick={() => setIsConvertModalOpen(false)}>×</button>
            </div>

            <form onSubmit={handleConvertLeadToClient} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', color: '#9ca3af', fontSize: 12, marginBottom: 4 }}>Nome Completo *</label>
                <input
                  type="text"
                  required
                  value={convertForm.name}
                  onChange={e => setConvertForm({ ...convertForm, name: e.target.value })}
                  style={{ width: '100%', padding: '9px 12px', background: '#262636', border: '1px solid #374151', borderRadius: 6, color: '#fff', fontSize: 13, boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#9ca3af', fontSize: 12, marginBottom: 4 }}>WhatsApp / Telefone</label>
                <input
                  type="text"
                  value={convertForm.phone}
                  onChange={e => setConvertForm({ ...convertForm, phone: maskPhone(e.target.value) })}
                  style={{ width: '100%', padding: '9px 12px', background: '#262636', border: '1px solid #374151', borderRadius: 6, color: '#fff', fontSize: 13, boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#9ca3af', fontSize: 12, marginBottom: 4 }}>Data de Aniversário *</label>
                <input
                  type="text"
                  required
                  placeholder="DD/MM/AAAA"
                  inputMode="numeric"
                  maxLength={10}
                  value={convertForm.birth_date}
                  onChange={e => setConvertForm({ ...convertForm, birth_date: maskDate(e.target.value) })}
                  style={{ width: '100%', padding: '9px 12px', background: '#262636', border: '1px solid #374151', borderRadius: 6, color: '#fff', fontSize: 13, boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#9ca3af', fontSize: 12, marginBottom: 4 }}>Apelido / Nickname (login na máquina)</label>
                <input
                  type="text"
                  placeholder="ex: player1"
                  value={convertForm.nickname}
                  onChange={e => setConvertForm({ ...convertForm, nickname: e.target.value })}
                  style={{ width: '100%', padding: '9px 12px', background: '#262636', border: '1px solid #374151', borderRadius: 6, color: '#fff', fontSize: 13, boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10 }}>
                <button
                  type="button"
                  onClick={() => setIsConvertModalOpen(false)}
                  style={{ padding: '9px 16px', background: '#374151', color: '#fff', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: 13 }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={converting}
                  style={{ padding: '9px 18px', background: '#10b981', color: '#fff', border: 0, borderRadius: 6, cursor: 'pointer', fontWeight: 'bold', fontSize: 13 }}
                >
                  {converting ? 'Cadastrando...' : 'Cadastrar e Vincular Cliente'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Automações da Coluna */}
      {isAutomationModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setIsAutomationModalOpen(false)}>
          <div style={{ background: '#14141f', border: '1px solid #374151', borderRadius: 12, padding: 24, width: 620, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0, color: '#f59e0b', fontSize: 18, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 6 }}>
                  ⚡ Automações & Disparos: {selectedColForAutomation?.name}
                </h3>
                <small style={{ color: '#9ca3af', fontSize: 12 }}>Configure rotinas automáticas de mensagens e disparos com controle anti-spam</small>
              </div>
              <button type="button" style={{ background: 'none', border: 0, color: '#9ca3af', fontSize: 22, cursor: 'pointer' }} onClick={() => setIsAutomationModalOpen(false)}>×</button>
            </div>

            {/* Lista de Automações Existentes */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontSize: 13, color: '#fff', fontWeight: 'bold' }}>Rotinas Ativas ({automationsList.length})</span>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditingAutomation(true);
                    setAutomationForm({
                      id: null,
                      name: `Disparo - ${selectedColForAutomation?.name || ''}`,
                      trigger_type: 'on_move',
                      delay_minutes: 0,
                      schedule_day: 'friday',
                      schedule_time: '17:00',
                      message_text: 'Olá {nome}! Sextouuuu Bora pra Arena Cyber Café! 🚀🎮',
                      instance_name: waInstanceName || 'CRMPages_5533998420631',
                      anti_spam_delay_sec: 10
                    });
                  }}
                  style={{ background: '#10b981', color: '#fff', border: 0, padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 'bold' }}
                >
                  + Nova Rotina / Automação
                </button>
              </div>

              {automationsList.length === 0 ? (
                <div style={{ background: '#111827', border: '1px dashed #374151', padding: 14, borderRadius: 8, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                  Nenhuma automação configurada para esta coluna. Clique em <b>+ Nova Rotina</b> para criar!
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {automationsList.map(a => (
                    <div key={a.id} style={{ background: '#1f2937', border: '1px solid #374151', padding: 12, borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 14, fontWeight: 'bold', color: '#fff' }}>{a.name}</span>
                          <span style={{
                            background: a.trigger_type === 'on_move' ? '#1e3a8a' : '#701a75',
                            color: a.trigger_type === 'on_move' ? '#60a5fa' : '#f0abfc',
                            fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 'bold'
                          }}>
                            {a.trigger_type === 'on_move' ? `⚡ Ao Mover (+${a.delay_minutes}m)` : `📅 Toda ${a.schedule_day} às ${a.schedule_time}`}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            type="button"
                            onClick={() => handleTriggerAutomationNow(a.id)}
                            disabled={runningBroadcast}
                            style={{ background: '#047857', color: '#fff', border: 0, padding: '4px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 'bold' }}
                            title="Disparar mensagem agora para todos os leads desta coluna"
                          >
                            🚀 Disparar Agora
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setAutomationForm(a);
                              setIsEditingAutomation(true);
                            }}
                            style={{ background: '#374151', color: '#fff', border: 0, padding: '4px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}
                          >
                            ✏️ Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteAutomation(a.id)}
                            style={{ background: '#7f1d1d', color: '#fff', border: 0, padding: '4px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 11 }}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>

                      <div style={{ color: '#d1d5db', fontSize: 12, background: '#111827', padding: '8px 10px', borderRadius: 6, fontStyle: 'italic', border: '1px solid #374151' }}>
                        "{a.message_text}"
                      </div>

                      <div style={{ display: 'flex', gap: 14, color: '#9ca3af', fontSize: 11 }}>
                        <span>📱 Instância: <b>{a.instance_name || 'Padrão'}</b></span>
                        <span>⏱️ Intervalo Anti-Spam: <b>{a.anti_spam_delay_sec || 10}s entre mensagens</b></span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Formulário de Criação / Edição de Automação */}
            {isEditingAutomation && (
              <form onSubmit={handleSaveAutomation} style={{ background: '#111827', border: '1px solid #3b82f6', borderRadius: 10, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <h4 style={{ margin: 0, color: '#60a5fa', fontSize: 14, fontWeight: 'bold' }}>
                  {automationForm.id ? '✏️ Editar Rotina de Automação' : '➕ Nova Rotina de Automação'}
                </h4>

                <div>
                  <label style={{ display: 'block', color: '#9ca3af', fontSize: 12, marginBottom: 4 }}>Nome da Rotina *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ex: Mensagem Sextou Arena"
                    value={automationForm.name}
                    onChange={e => setAutomationForm({ ...automationForm, name: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', background: '#262636', border: '1px solid #374151', borderRadius: 6, color: '#fff', fontSize: 13, boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: '#9ca3af', fontSize: 12, marginBottom: 4 }}>Gatilho do Disparo *</label>
                    <select
                      value={automationForm.trigger_type}
                      onChange={e => setAutomationForm({ ...automationForm, trigger_type: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', background: '#262636', border: '1px solid #374151', borderRadius: 6, color: '#fff', fontSize: 13, boxSizing: 'border-box' }}
                    >
                      <option value="on_move">⚡ Ao Mover Lead para esta Coluna</option>
                      <option value="recurring_weekly">📅 Disparo Recorrente Semanal (Ex: Sexta-Feira)</option>
                    </select>
                  </div>

                  {automationForm.trigger_type === 'on_move' ? (
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', color: '#9ca3af', fontSize: 12, marginBottom: 4 }}>Tempo de Espera (Minutos)</label>
                      <input
                        type="number"
                        min="0"
                        placeholder="0 = Imediato"
                        value={automationForm.delay_minutes}
                        onChange={e => setAutomationForm({ ...automationForm, delay_minutes: parseInt(e.target.value) || 0 })}
                        style={{ width: '100%', padding: '8px 12px', background: '#262636', border: '1px solid #374151', borderRadius: 6, color: '#fff', fontSize: 13, boxSizing: 'border-box' }}
                      />
                    </div>
                  ) : (
                    <>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', color: '#9ca3af', fontSize: 12, marginBottom: 4 }}>Dia da Semana</label>
                        <select
                          value={automationForm.schedule_day}
                          onChange={e => setAutomationForm({ ...automationForm, schedule_day: e.target.value })}
                          style={{ width: '100%', padding: '8px 12px', background: '#262636', border: '1px solid #374151', borderRadius: 6, color: '#fff', fontSize: 13, boxSizing: 'border-box' }}
                        >
                          <option value="friday">Sexta-feira</option>
                          <option value="saturday">Sábado</option>
                          <option value="sunday">Domingo</option>
                          <option value="monday">Segunda-feira</option>
                          <option value="tuesday">Terça-feira</option>
                          <option value="wednesday">Quarta-feira</option>
                          <option value="thursday">Quinta-feira</option>
                        </select>
                      </div>

                      <div style={{ width: 100 }}>
                        <label style={{ display: 'block', color: '#9ca3af', fontSize: 12, marginBottom: 4 }}>Horário</label>
                        <input
                          type="time"
                          value={automationForm.schedule_time}
                          onChange={e => setAutomationForm({ ...automationForm, schedule_time: e.target.value })}
                          style={{ width: '100%', padding: '8px 12px', background: '#262636', border: '1px solid #374151', borderRadius: 6, color: '#fff', fontSize: 13, boxSizing: 'border-box' }}
                        />
                      </div>
                    </>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: '#9ca3af', fontSize: 12, marginBottom: 4 }}>Instância do WhatsApp de Disparo</label>
                    <select
                      value={automationForm.instance_name || 'CRMPages_5533998420631'}
                      onChange={e => setAutomationForm({ ...automationForm, instance_name: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', background: '#262636', border: '1px solid #374151', borderRadius: 6, color: '#fff', fontSize: 13, boxSizing: 'border-box' }}
                    >
                      {availableInstances.length === 0 ? (
                        <option value="CRMPages_5533998420631">📱 CRMPages_5533998420631 (Padrão Arena)</option>
                      ) : (
                        availableInstances.map(inst => (
                          <option key={inst.instance_name} value={inst.instance_name}>
                            📱 {inst.instance_name} ({inst.status || 'ativa'})
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  <div style={{ width: 180 }}>
                    <label style={{ display: 'block', color: '#f59e0b', fontSize: 12, marginBottom: 4, fontWeight: 'bold' }}>⏱️ Anti-Spam (Segs)</label>
                    <input
                      type="number"
                      min="2"
                      max="120"
                      placeholder="Segundos entre msgs"
                      value={automationForm.anti_spam_delay_sec}
                      onChange={e => setAutomationForm({ ...automationForm, anti_spam_delay_sec: parseInt(e.target.value) || 10 })}
                      style={{ width: '100%', padding: '8px 12px', background: '#262636', border: '1px solid #f59e0b', borderRadius: 6, color: '#fff', fontSize: 13, boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', color: '#9ca3af', fontSize: 12, marginBottom: 4 }}>Mensagem a ser enviada no WhatsApp *</label>
                  <textarea
                    rows={3}
                    required
                    placeholder="Olá {nome}! Sextouuuu Bora pra Arena... ou Estamos Abertos Pessoal!"
                    value={automationForm.message_text}
                    onChange={e => setAutomationForm({ ...automationForm, message_text: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', background: '#262636', border: '1px solid #374151', borderRadius: 6, color: '#fff', fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' }}
                  />
                  <small style={{ color: '#9ca3af', fontSize: 11 }}>💡 Dica: Use <b>{'{nome}'}</b> para ser substituído pelo nome do lead automaticamente.</small>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 6 }}>
                  <button
                    type="button"
                    onClick={() => setIsEditingAutomation(false)}
                    style={{ padding: '8px 14px', background: '#374151', color: '#fff', border: 0, borderRadius: 6, cursor: 'pointer', fontSize: 12 }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    style={{ padding: '8px 16px', background: '#3b82f6', color: '#fff', border: 0, borderRadius: 6, cursor: 'pointer', fontWeight: 'bold', fontSize: 12 }}
                  >
                    💾 Salvar Automação
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

const styles = {
  pencilBtn: { border: 0, background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: 13, opacity: 0.8, transition: 'opacity 0.2s', padding: '0 2px' },
  colGapInsertBtnWrapper: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 14, margin: '0 -7px', zIndex: 10 },
  colGapInsertBtn: { width: 22, height: 22, borderRadius: '50%', background: '#3b82f6', color: '#fff', border: '2px solid #111827', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 'bold', boxShadow: '0 2px 6px rgba(0,0,0,0.5)', opacity: 0.75, transition: 'all 0.2s' },
  addColumnCard: { width: 200, minWidth: 200, minHeight: 180, background: '#111827', border: '2px dashed #374151', borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#9ca3af', gap: 6, transition: 'all 0.2s' },
  page: { padding: 20, height: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  headerTitle: { display: 'flex', alignItems: 'center', gap: 12 },
  modalActions: { display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 },
  fieldGroup: { marginBottom: 16 },
  primaryAction: { padding: '8px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 },
  secondaryAction: { padding: '8px 16px', background: 'transparent', border: '1px solid #374151', color: '#9ca3af', borderRadius: 6, cursor: 'pointer' },
  headerActions: { display: 'flex', gap: 12, alignItems: 'center' },
  title: { color: '#fff', margin: 0, fontSize: 24 },
  badge: { background: '#3b82f6', color: '#fff', padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700 },
  select: { padding: '8px 12px', background: '#1f2937', color: '#fff', border: '1px solid #374151', borderRadius: 8, outline: 'none' },
  btnPrimary: { background: '#3b82f6', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { background: '#374151', color: '#fff', border: '1px solid #4b5563', padding: '8px 16px', borderRadius: 8, fontWeight: 600, cursor: 'pointer' },
  kanbanBoard: { display: 'flex', gap: 16, overflowX: 'auto', flex: 1, paddingTop: 14, paddingBottom: 10, paddingLeft: 4, paddingRight: 4 },
  column: { width: 300, minWidth: 300, background: '#111827', borderRadius: 8, display: 'flex', flexDirection: 'column' },
  columnHeader: { padding: '12px 16px', borderTop: '4px solid', borderTopColor: '#3b82f6', background: '#1f2937', borderRadius: '8px 8px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  colName: { color: '#f3f4f6', fontWeight: 600, fontSize: 14 },
  colCount: { background: '#374151', color: '#d1d5db', padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 700 },
  leadList: { flex: 1, padding: 12, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 },
  leadCard: { background: '#1f2937', padding: 14, borderRadius: 8, border: '1px solid #374151', cursor: 'grab', display: 'flex', flexDirection: 'column', gap: 6 },
  leadName: { color: '#f9fafb', fontSize: 14, fontWeight: 600 },
  leadPhone: { color: '#9ca3af', fontSize: 12 },
  tagList: { display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 },
  tag: { padding: '2px 6px', borderRadius: 4, fontSize: 10, color: '#fff', fontWeight: 600, textTransform: 'uppercase' },
  
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: '#111827', width: '90%', height: '90%', borderRadius: 12, display: 'flex', flexDirection: 'column', border: '1px solid #374151', overflow: 'hidden' },
  modalHeader: { padding: 20, borderBottom: '1px solid #1f2937', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalTitle: { margin: 0, color: '#fff', fontSize: 18 },
  closeBtn: { background: 'transparent', color: '#9ca3af', border: 'none', fontSize: 24, cursor: 'pointer' },
  modalBody: { display: 'flex', flex: 1, overflow: 'hidden' },
  
  chatSection: { flex: 2, display: 'flex', flexDirection: 'column', borderRight: '1px solid #1f2937', background: '#030712' },
  chatHistory: { flex: 1, padding: 20, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 },
  messageIn: { background: '#1f2937', padding: '10px 14px', borderRadius: '12px 12px 12px 0', color: '#f3f4f6', maxWidth: '75%', alignSelf: 'flex-start', fontSize: 14 },
  messageOut: { background: '#15803d', padding: '10px 14px', borderRadius: '12px 12px 0 12px', color: '#fff', maxWidth: '75%', alignSelf: 'flex-end', fontSize: 14 },
  messageTime: { fontSize: 10, color: 'rgba(255,255,255,0.6)', marginTop: 4, textAlign: 'right' },
  mutedText: { color: '#6b7280', textAlign: 'center', marginTop: 40, fontSize: 14 },
  chatInputRow: { display: 'flex', padding: 16, borderTop: '1px solid #1f2937', background: '#111827', gap: 10 },
  chatInput: { flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid #374151', background: '#1f2937', color: '#fff' },
  sendBtn: { background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, padding: '0 20px', fontWeight: 600, cursor: 'pointer' },
  
  detailsSection: { flex: 1, background: '#111827', padding: 20, overflowY: 'auto' },
  detailsTitle: { color: '#f3f4f6', margin: '0 0 20px 0', fontSize: 16, borderBottom: '1px solid #1f2937', paddingBottom: 10 },
  fieldGroup: { marginBottom: 16 },
  label: { display: 'block', color: '#9ca3af', fontSize: 12, marginBottom: 6 },
  input: { width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #374151', background: '#1f2937', color: '#fff', boxSizing: 'border-box' },
  actions: { display: 'flex', flexDirection: 'column', gap: 10, marginTop: 30 },
  primaryAction: { background: '#10b981', color: '#fff', padding: 12, borderRadius: 8, border: 'none', fontWeight: 600, cursor: 'pointer' },
  secondaryAction: { background: '#374151', color: '#fff', padding: 12, borderRadius: 8, border: 'none', fontWeight: 600, cursor: 'pointer' }
};
